import React, { useState, useEffect, useMemo } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Snackbar,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import HistoryIcon from '@mui/icons-material/History';
import PageWithHeader from '../common/PageWithHeader';
import { useRouter } from '../../hooks/useRouter';
import { useAppSelector } from '../../redux/hooks';
import {
  useGetMyCastingToolsQuery,
  useGetCastingToolByPeriodQuery,
  usePostCastingToolMutation,
  usePatchCastingToolByIdMutation,
  CastingToolEntries,
} from '../../redux/castingToolApi';

// Helper to get available months for submission (current and past 12 months)
const getAvailableMonths = (): string[] => {
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  const result: string[] = [];
  const now = new Date();
  
  for (let i = 0; i < 12; i++) {
    const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
    result.push(`${months[date.getMonth()]} ${date.getFullYear()}`);
  }
  
  return result;
};

// Get current period
const getCurrentPeriod = (): string => {
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  const now = new Date();
  return `${months[now.getMonth()]} ${now.getFullYear()}`;
};

// Empty entries template
const emptyEntries: CastingToolEntries = {
  alert: '',
  curious: '',
  responsive: '',
  resourceful: '',
  faster: '',
  easier: '',
  bigger: '',
  better: '',
  newCommitment: '',
  newCourage: '',
  newCapability: '',
  newConfidence: '',
  showUpOnTime: '',
  doWhatYouSay: '',
  finishWhatYouStart: '',
  sayPleaseThankYou: '',
};

const CastingTool: React.FC = () => {
  const router = useRouter();
  const currentUser = useAppSelector(state => state.auth.user);
  
  // Period selection
  const availableMonths = useMemo(() => getAvailableMonths(), []);
  const [selectedPeriod, setSelectedPeriod] = useState<string>(getCurrentPeriod());
  
  // Form state
  const [entries, setEntries] = useState<CastingToolEntries>(emptyEntries);
  const [existingId, setExistingId] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  
  // Snackbar state
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });
  
  // API hooks
  const { data: mySubmissions } = useGetMyCastingToolsQuery();
  const { data: existingEntry, isLoading: isLoadingEntry, refetch } = useGetCastingToolByPeriodQuery(
    { reviewPeriod: selectedPeriod },
    { skip: !selectedPeriod }
  );
  const [createCastingTool, { isLoading: isCreating }] = usePostCastingToolMutation();
  const [updateCastingTool, { isLoading: isUpdating }] = usePatchCastingToolByIdMutation();
  
  const isSaving = isCreating || isUpdating;
  
  // Compute which periods already have submissions
  const submittedPeriods = useMemo(() => {
    return new Set(mySubmissions?.map(s => s.reviewPeriod) || []);
  }, [mySubmissions]);
  
  // Load existing entry when period changes
  useEffect(() => {
    if (existingEntry) {
      setEntries(existingEntry.entries);
      setExistingId(existingEntry.id);
      setHasChanges(false);
    } else {
      setEntries(emptyEntries);
      setExistingId(null);
      setHasChanges(false);
    }
  }, [existingEntry, selectedPeriod]);
  
  // Handle period change
  const handlePeriodChange = (event: SelectChangeEvent) => {
    setSelectedPeriod(event.target.value);
  };
  
  // Handle entry change
  const updateEntry = (field: keyof CastingToolEntries, value: string) => {
    setEntries(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };
  
  // Handle save
  const handleSave = async () => {
    try {
      if (existingId) {
        await updateCastingTool({
          id: existingId,
          castingToolData: { entries },
        }).unwrap();
        setSnackbar({ open: true, message: 'Casting tool updated successfully!', severity: 'success' });
      } else {
        const result = await createCastingTool({
          castingToolData: {
            reviewPeriod: selectedPeriod,
            entries,
          },
        }).unwrap();
        setExistingId(result.id);
        setSnackbar({ open: true, message: 'Casting tool saved successfully!', severity: 'success' });
      }
      setHasChanges(false);
      refetch();
    } catch (error) {
      console.error('Error saving casting tool:', error);
      setSnackbar({ open: true, message: 'Error saving casting tool. Please try again.', severity: 'error' });
    }
  };

  const colWidths = ['3.45%', '3.45%', '14.84%', '28.24%', '3.44%', '3.44%', '14.86%', '28.28%'];

  const commonTableCell = {
    border: '1.5px solid #231F20',
    padding: '6px 8px',
    fontFamily: 'Arial, Helvetica, sans-serif',
    color: '#231F20',
    verticalAlign: 'middle',
  } as const;

  const numberCell = {
    ...commonTableCell,
    backgroundColor: '#231F20',
    color: '#FFFFFF',
    fontWeight: 700,
    textAlign: 'center',
    fontSize: '18px',
    lineHeight: 1,
    padding: '4px 0',
  } as const;

  return (
    <PageWithHeader>
      <Box sx={{ minHeight: '100%', background: '#ECECEC', py: 3 }}>
        <Paper
          elevation={8}
          sx={{
            maxWidth: 1060,
            mx: 'auto',
            p: { xs: 1.5, sm: 2, md: 2.5 },
            borderRadius: 1.5,
            backgroundColor: '#FFFFFF',
            border: '1px solid #B8B8B8',
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton onClick={() => router.push('/reviews')} sx={{ border: '1px solid #C9C9C9' }}>
                <ArrowBackIcon />
              </IconButton>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button 
                variant="outlined" 
                size="small" 
                startIcon={<HistoryIcon />}
                onClick={() => router.push('/reviews/castingtool/historic')}
              >
                View History
              </Button>
              <Button 
                variant="contained" 
                size="small" 
                startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                onClick={handleSave}
                disabled={isSaving || !hasChanges}
                color="primary"
              >
                {existingId ? 'Update' : 'Save'}
              </Button>
            </Box>
          </Box>

          <Box sx={{ px: { xs: 0.25, sm: 0.5, md: 1 }, pb: 0.5 }}>
            <Typography
              sx={{
                fontFamily: 'Arial, Helvetica, sans-serif',
                fontWeight: 700,
                color: '#00AEEF',
                lineHeight: 1,
                display: 'flex',
                alignItems: 'baseline',
                justifyContent: 'center',
                gap: 0.5,
                fontSize: { xs: 34, sm: 52, md: 68 },
              }}
            >
              <span>4</span>
              <span style={{ fontSize: '0.7em' }}>x</span>
              <span>4</span>
              <span style={{ fontSize: '0.46em', color: '#231F20', fontWeight: 700 }}>Casting Tool</span>
            </Typography>

            <Box sx={{ display: 'flex', mt: 1.25, mb: 1.25, gap: 3, flexWrap: 'wrap' }}>
              <Box sx={{ flex: 1, minWidth: 200 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Review Period</InputLabel>
                  <Select
                    value={selectedPeriod}
                    label="Review Period"
                    onChange={handlePeriodChange}
                    disabled={isLoadingEntry}
                  >
                    {availableMonths.map((month) => (
                      <MenuItem key={month} value={month}>
                        {month} {submittedPeriods.has(month) ? '✓' : ''}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 200 }}>
                <Typography sx={{ fontFamily: 'Arial, Helvetica, sans-serif', fontSize: 18, color: '#231F20', whiteSpace: 'nowrap' }}>Name:</Typography>
                <Typography sx={{ fontFamily: 'Arial, Helvetica, sans-serif', fontSize: 18, color: '#231F20', borderBottom: '1.5px solid #231F20', flex: 1, minHeight: 20 }}>{currentUser?.name ?? ''}</Typography>
              </Box>
              <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 200 }}>
                <Typography sx={{ fontFamily: 'Arial, Helvetica, sans-serif', fontSize: 18, color: '#231F20', whiteSpace: 'nowrap' }}>Role:</Typography>
                <Typography sx={{ fontFamily: 'Arial, Helvetica, sans-serif', fontSize: 18, color: '#231F20', borderBottom: '1.5px solid #231F20', flex: 1, minHeight: 20 }}>{currentUser?.jobTitle ?? ''}</Typography>
              </Box>
            </Box>
            
            {isLoadingEntry && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={24} />
                <Typography sx={{ ml: 1 }}>Loading...</Typography>
              </Box>
            )}

            <Box sx={{ overflowX: 'auto', border: '1.5px solid #231F20' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed', minWidth: 960 }}>
                <colgroup>
                  {colWidths.map((width, i) => (
                    <col key={i} style={{ width }} />
                  ))}
                </colgroup>
                <tbody>
                  <tr>
                    <td colSpan={2} style={{ ...commonTableCell, backgroundColor: '#EC7E1E', color: '#FFFFFF', fontWeight: 700, fontSize: 20, textAlign: 'center' }}>1</td>
                    <td colSpan={2} style={{ ...commonTableCell, backgroundColor: '#EC7E1E', color: '#FFFFFF', fontWeight: 700, fontSize: 20, textAlign: 'center' }}>Best Performance: Better</td>
                    <td colSpan={4} style={{ ...commonTableCell, backgroundColor: '#8B9B3C', color: '#FFFFFF', fontWeight: 700, fontSize: 20, textAlign: 'center' }}>2 Best Results: Bigger</td>
                  </tr>

                  <tr>
                    <td style={{ ...numberCell, width: '3.45%' }}>1</td>
                    <td colSpan={2} style={{ ...commonTableCell, backgroundColor: '#FEEEE0', fontWeight: 700, fontSize: 28, textAlign: 'center' }}>Alert</td>
                    <td style={{ ...commonTableCell, backgroundColor: '#FFFFFF', padding: 0, minHeight: 56 }}><textarea value={entries.alert} onChange={e => updateEntry('alert', e.target.value)} style={{ width: '100%', minHeight: 56, border: 'none', resize: 'vertical', fontFamily: 'Arial, Helvetica, sans-serif', fontSize: 14, color: '#231F20', background: 'transparent', padding: '6px 8px', boxSizing: 'border-box', outline: 'none', display: 'block' }} /></td>
                    <td style={{ ...numberCell, width: '3.44%' }}>1</td>
                    <td colSpan={2} style={{ ...commonTableCell, backgroundColor: '#EDEFE2', fontWeight: 700, fontSize: 28, textAlign: 'center' }}>Faster</td>
                    <td style={{ ...commonTableCell, backgroundColor: '#FFFFFF', padding: 0, minHeight: 56 }}><textarea value={entries.faster} onChange={e => updateEntry('faster', e.target.value)} style={{ width: '100%', minHeight: 56, border: 'none', resize: 'vertical', fontFamily: 'Arial, Helvetica, sans-serif', fontSize: 14, color: '#231F20', background: 'transparent', padding: '6px 8px', boxSizing: 'border-box', outline: 'none', display: 'block' }} /></td>
                  </tr>

                  <tr>
                    <td style={{ ...numberCell, width: '3.45%' }}>2</td>
                    <td colSpan={2} style={{ ...commonTableCell, backgroundColor: '#FEEEE0', fontWeight: 700, fontSize: 28, textAlign: 'center' }}>Curious</td>
                    <td style={{ ...commonTableCell, backgroundColor: '#FFFFFF', padding: 0, minHeight: 56 }}><textarea value={entries.curious} onChange={e => updateEntry('curious', e.target.value)} style={{ width: '100%', minHeight: 56, border: 'none', resize: 'vertical', fontFamily: 'Arial, Helvetica, sans-serif', fontSize: 14, color: '#231F20', background: 'transparent', padding: '6px 8px', boxSizing: 'border-box', outline: 'none', display: 'block' }} /></td>
                    <td style={{ ...numberCell, width: '3.44%' }}>2</td>
                    <td colSpan={2} style={{ ...commonTableCell, backgroundColor: '#EDEFE2', fontWeight: 700, fontSize: 28, textAlign: 'center' }}>Easier</td>
                    <td style={{ ...commonTableCell, backgroundColor: '#FFFFFF', padding: 0, minHeight: 56 }}><textarea value={entries.easier} onChange={e => updateEntry('easier', e.target.value)} style={{ width: '100%', minHeight: 56, border: 'none', resize: 'vertical', fontFamily: 'Arial, Helvetica, sans-serif', fontSize: 14, color: '#231F20', background: 'transparent', padding: '6px 8px', boxSizing: 'border-box', outline: 'none', display: 'block' }} /></td>
                  </tr>

                  <tr>
                    <td style={{ ...numberCell, width: '3.45%' }}>3</td>
                    <td colSpan={2} style={{ ...commonTableCell, backgroundColor: '#FEEEE0', fontWeight: 700, fontSize: 28, textAlign: 'center' }}>Responsive</td>
                    <td style={{ ...commonTableCell, backgroundColor: '#FFFFFF', padding: 0, minHeight: 56 }}><textarea value={entries.responsive} onChange={e => updateEntry('responsive', e.target.value)} style={{ width: '100%', minHeight: 56, border: 'none', resize: 'vertical', fontFamily: 'Arial, Helvetica, sans-serif', fontSize: 14, color: '#231F20', background: 'transparent', padding: '6px 8px', boxSizing: 'border-box', outline: 'none', display: 'block' }} /></td>
                    <td style={{ ...numberCell, width: '3.44%' }}>3</td>
                    <td colSpan={2} style={{ ...commonTableCell, backgroundColor: '#EDEFE2', fontWeight: 700, fontSize: 28, textAlign: 'center' }}>Bigger</td>
                    <td style={{ ...commonTableCell, backgroundColor: '#FFFFFF', padding: 0, minHeight: 56 }}><textarea value={entries.bigger} onChange={e => updateEntry('bigger', e.target.value)} style={{ width: '100%', minHeight: 56, border: 'none', resize: 'vertical', fontFamily: 'Arial, Helvetica, sans-serif', fontSize: 14, color: '#231F20', background: 'transparent', padding: '6px 8px', boxSizing: 'border-box', outline: 'none', display: 'block' }} /></td>
                  </tr>

                  <tr>
                    <td style={{ ...numberCell, width: '3.45%' }}>4</td>
                    <td colSpan={2} style={{ ...commonTableCell, backgroundColor: '#FEEEE0', fontWeight: 700, fontSize: 28, textAlign: 'center' }}>Resourceful</td>
                    <td style={{ ...commonTableCell, backgroundColor: '#FFFFFF', padding: 0, minHeight: 56 }}><textarea value={entries.resourceful} onChange={e => updateEntry('resourceful', e.target.value)} style={{ width: '100%', minHeight: 56, border: 'none', resize: 'vertical', fontFamily: 'Arial, Helvetica, sans-serif', fontSize: 14, color: '#231F20', background: 'transparent', padding: '6px 8px', boxSizing: 'border-box', outline: 'none', display: 'block' }} /></td>
                    <td style={{ ...numberCell, width: '3.44%' }}>4</td>
                    <td colSpan={2} style={{ ...commonTableCell, backgroundColor: '#EDEFE2', fontWeight: 700, fontSize: 28, textAlign: 'center' }}>Better</td>
                    <td style={{ ...commonTableCell, backgroundColor: '#FFFFFF', padding: 0, minHeight: 56 }}><textarea value={entries.better} onChange={e => updateEntry('better', e.target.value)} style={{ width: '100%', minHeight: 56, border: 'none', resize: 'vertical', fontFamily: 'Arial, Helvetica, sans-serif', fontSize: 14, color: '#231F20', background: 'transparent', padding: '6px 8px', boxSizing: 'border-box', outline: 'none', display: 'block' }} /></td>
                  </tr>

                  <tr>
                    <td colSpan={2} style={{ ...commonTableCell, backgroundColor: '#7399B1', color: '#FFFFFF', fontWeight: 700, fontSize: 20, textAlign: 'center' }}>3</td>
                    <td colSpan={2} style={{ ...commonTableCell, backgroundColor: '#7399B1', color: '#FFFFFF', fontWeight: 700, fontSize: 20, textAlign: 'center' }}>BEING A HERO</td>
                    <td colSpan={4} style={{ ...commonTableCell, backgroundColor: '#641833', color: '#FFFFFF', fontWeight: 700, fontSize: 20, textAlign: 'center' }}>
                      4 NOT DRIVING OTHERS CRAZY
                    </td>
                  </tr>

                  <tr>
                    <td style={{ ...numberCell, width: '3.45%' }}>1</td>
                    <td colSpan={2} style={{ ...commonTableCell, backgroundColor: '#E9EDF2', fontWeight: 700, fontSize: 22, textAlign: 'center' }}>New Commitment</td>
                    <td style={{ ...commonTableCell, backgroundColor: '#FFFFFF', padding: 0, minHeight: 56 }}><textarea value={entries.newCommitment} onChange={e => updateEntry('newCommitment', e.target.value)} style={{ width: '100%', minHeight: 56, border: 'none', resize: 'vertical', fontFamily: 'Arial, Helvetica, sans-serif', fontSize: 14, color: '#231F20', background: 'transparent', padding: '6px 8px', boxSizing: 'border-box', outline: 'none', display: 'block' }} /></td>
                    <td style={{ ...numberCell, width: '3.44%' }}>1</td>
                    <td colSpan={2} style={{ ...commonTableCell, backgroundColor: '#E6DADB', fontWeight: 700, fontSize: 22, textAlign: 'center' }}>Show Up On Time</td>
                    <td style={{ ...commonTableCell, backgroundColor: '#FFFFFF', padding: 0, minHeight: 56 }}><textarea value={entries.showUpOnTime} onChange={e => updateEntry('showUpOnTime', e.target.value)} style={{ width: '100%', minHeight: 56, border: 'none', resize: 'vertical', fontFamily: 'Arial, Helvetica, sans-serif', fontSize: 14, color: '#231F20', background: 'transparent', padding: '6px 8px', boxSizing: 'border-box', outline: 'none', display: 'block' }} /></td>
                  </tr>

                  <tr>
                    <td style={{ ...numberCell, width: '3.45%' }}>2</td>
                    <td colSpan={2} style={{ ...commonTableCell, backgroundColor: '#E9EDF2', fontWeight: 700, fontSize: 22, textAlign: 'center' }}>New Courage</td>
                    <td style={{ ...commonTableCell, backgroundColor: '#FFFFFF', padding: 0, minHeight: 56 }}><textarea value={entries.newCourage} onChange={e => updateEntry('newCourage', e.target.value)} style={{ width: '100%', minHeight: 56, border: 'none', resize: 'vertical', fontFamily: 'Arial, Helvetica, sans-serif', fontSize: 14, color: '#231F20', background: 'transparent', padding: '6px 8px', boxSizing: 'border-box', outline: 'none', display: 'block' }} /></td>
                    <td style={{ ...numberCell, width: '3.44%' }}>2</td>
                    <td colSpan={2} style={{ ...commonTableCell, backgroundColor: '#E6DADB', fontWeight: 700, fontSize: 22, textAlign: 'center' }}>Do What You Say</td>
                    <td style={{ ...commonTableCell, backgroundColor: '#FFFFFF', padding: 0, minHeight: 56 }}><textarea value={entries.doWhatYouSay} onChange={e => updateEntry('doWhatYouSay', e.target.value)} style={{ width: '100%', minHeight: 56, border: 'none', resize: 'vertical', fontFamily: 'Arial, Helvetica, sans-serif', fontSize: 14, color: '#231F20', background: 'transparent', padding: '6px 8px', boxSizing: 'border-box', outline: 'none', display: 'block' }} /></td>
                  </tr>

                  <tr>
                    <td style={{ ...numberCell, width: '3.45%' }}>3</td>
                    <td colSpan={2} style={{ ...commonTableCell, backgroundColor: '#E9EDF2', fontWeight: 700, fontSize: 22, textAlign: 'center' }}>New Capability</td>
                    <td style={{ ...commonTableCell, backgroundColor: '#FFFFFF', padding: 0, minHeight: 56 }}><textarea value={entries.newCapability} onChange={e => updateEntry('newCapability', e.target.value)} style={{ width: '100%', minHeight: 56, border: 'none', resize: 'vertical', fontFamily: 'Arial, Helvetica, sans-serif', fontSize: 14, color: '#231F20', background: 'transparent', padding: '6px 8px', boxSizing: 'border-box', outline: 'none', display: 'block' }} /></td>
                    <td style={{ ...numberCell, width: '3.44%' }}>3</td>
                    <td colSpan={2} style={{ ...commonTableCell, backgroundColor: '#E6DADB', fontWeight: 700, fontSize: 22, textAlign: 'center' }}>Finish What You Start</td>
                    <td style={{ ...commonTableCell, backgroundColor: '#FFFFFF', padding: 0, minHeight: 56 }}><textarea value={entries.finishWhatYouStart} onChange={e => updateEntry('finishWhatYouStart', e.target.value)} style={{ width: '100%', minHeight: 56, border: 'none', resize: 'vertical', fontFamily: 'Arial, Helvetica, sans-serif', fontSize: 14, color: '#231F20', background: 'transparent', padding: '6px 8px', boxSizing: 'border-box', outline: 'none', display: 'block' }} /></td>
                  </tr>

                  <tr>
                    <td style={{ ...numberCell, width: '3.45%' }}>4</td>
                    <td colSpan={2} style={{ ...commonTableCell, backgroundColor: '#E9EDF2', fontWeight: 700, fontSize: 22, textAlign: 'center' }}>New Confidence</td>
                    <td style={{ ...commonTableCell, backgroundColor: '#FFFFFF', padding: 0, minHeight: 56 }}><textarea value={entries.newConfidence} onChange={e => updateEntry('newConfidence', e.target.value)} style={{ width: '100%', minHeight: 56, border: 'none', resize: 'vertical', fontFamily: 'Arial, Helvetica, sans-serif', fontSize: 14, color: '#231F20', background: 'transparent', padding: '6px 8px', boxSizing: 'border-box', outline: 'none', display: 'block' }} /></td>
                    <td style={{ ...numberCell, width: '3.44%' }}>4</td>
                    <td colSpan={2} style={{ ...commonTableCell, backgroundColor: '#E6DADB', fontWeight: 700, fontSize: 22, textAlign: 'center' }}>Say Please &amp; Thank You</td>
                    <td style={{ ...commonTableCell, backgroundColor: '#FFFFFF', padding: 0, minHeight: 56 }}><textarea value={entries.sayPleaseThankYou} onChange={e => updateEntry('sayPleaseThankYou', e.target.value)} style={{ width: '100%', minHeight: 56, border: 'none', resize: 'vertical', fontFamily: 'Arial, Helvetica, sans-serif', fontSize: 14, color: '#231F20', background: 'transparent', padding: '6px 8px', boxSizing: 'border-box', outline: 'none', display: 'block' }} /></td>
                  </tr>
                </tbody>
              </table>
            </Box>


          </Box>
        </Paper>
      </Box>
      
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </PageWithHeader>
  );
};

export default CastingTool;